import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Calendar, Tag, Check, X, Edit, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { formatarData } from "@/hooks/useBlog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Artigo {
  id: string;
  titulo: string;
  slug: string;
  resumo: string | null;
  conteudo_html: string;
  imagem_destaque_url: string | null;
  categoria_id: string | null;
  seo_title: string | null;
  seo_description: string | null;
  publicado: boolean;
  destaque: boolean;
  data_publicacao: string;
  status_aprovacao: string | null;
  aprovado_por: string | null;
  aprovado_em: string | null;
  categoria?: { nome: string; slug: string } | null;
}

export default function AdminPreview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: artigo, isLoading, error } = useQuery({
    queryKey: ["admin-artigo-preview", id],
    queryFn: async (): Promise<Artigo | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("artigos")
        .select(`
          *,
          categoria:categorias_blog(nome, slug)
        `)
        .eq("id", id)
        .single();

      if (error) throw error;
      return data as Artigo;
    },
    enabled: !!id,
  });

  const approvalMutation = useMutation({
    mutationFn: async ({ approved }: { approved: boolean }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const updateData = approved
        ? {
            status_aprovacao: "aprovado",
            aprovado_por: user?.id,
            aprovado_em: new Date().toISOString(),
          }
        : {
            status_aprovacao: "rascunho",
            aprovado_por: null,
            aprovado_em: null,
          };

      const { error } = await supabase
        .from("artigos")
        .update(updateData)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: (_, { approved }) => {
      queryClient.invalidateQueries({ queryKey: ["admin-artigo-preview", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-artigos"] });
      queryClient.invalidateQueries({ queryKey: ["admin-stats"] });
      queryClient.invalidateQueries({ queryKey: ["admin-pendentes"] });
      
      toast({
        title: approved ? "Artigo aprovado!" : "Artigo devolvido para edição",
        description: approved
          ? "O artigo foi aprovado e pode ser agendado/publicado."
          : "O artigo voltou para o status de rascunho.",
      });

      if (approved) {
        navigate(`/admin/artigos/${id}`);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Erro",
        description: error.message || "Não foi possível processar a aprovação.",
        variant: "destructive",
      });
    },
  });

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case "aguardando_aprovacao":
        return <Badge variant="secondary" className="bg-amber-100 text-amber-800">Aguardando Aprovação</Badge>;
      case "aprovado":
        return <Badge variant="default" className="bg-green-100 text-green-800">Aprovado</Badge>;
      default:
        return <Badge variant="outline">Rascunho</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-64 w-full rounded-xl" />
        <Skeleton className="h-10 w-3/4" />
        <div className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    );
  }

  if (error || !artigo) {
    return (
      <div className="text-center py-12">
        <h1 className="text-2xl font-bold text-foreground mb-4">
          Artigo não encontrado
        </h1>
        <p className="text-muted-foreground mb-8">
          O artigo que você procura não existe ou foi removido.
        </p>
        <Button onClick={() => navigate("/admin/artigos")}>
          Voltar aos Artigos
        </Button>
      </div>
    );
  }

  const isAwaitingApproval = artigo.status_aprovacao === "aguardando_aprovacao";

  return (
    <>
      <Helmet>
        <title>Preview: {artigo.titulo} | Aceena Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="space-y-6">
        {/* Header com ações */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 pb-4 border-b border-border">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/admin/artigos">
                <ArrowLeft className="h-5 w-5" />
              </Link>
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground">Preview do Artigo</h1>
              <p className="text-sm text-muted-foreground">
                Visualize como o artigo ficará no blog
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            {getStatusBadge(artigo.status_aprovacao)}
            
            <Button variant="outline" asChild>
              <Link to={`/admin/artigos/${artigo.id}`}>
                <Edit className="h-4 w-4 mr-2" />
                Editar
              </Link>
            </Button>

            {isAwaitingApproval && (
              <>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                      <X className="h-4 w-4 mr-2" />
                      Devolver
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Devolver para edição?</AlertDialogTitle>
                      <AlertDialogDescription>
                        O artigo voltará para o status de rascunho e o autor poderá fazer alterações.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => approvalMutation.mutate({ approved: false })}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Devolver
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                <Button
                  onClick={() => approvalMutation.mutate({ approved: true })}
                  disabled={approvalMutation.isPending}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {approvalMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-2" />
                  )}
                  Aprovar
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Preview do artigo (simulando o blog público) */}
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="bg-muted/50 px-6 py-3 border-b border-border">
            <p className="text-xs text-muted-foreground font-mono">
              aceena.com.br/blog/{artigo.slug}
            </p>
          </div>
          
          <article className="p-6 lg:p-10 max-w-4xl mx-auto">
            {/* Imagem de destaque */}
            {artigo.imagem_destaque_url && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8 rounded-xl overflow-hidden bg-muted"
              >
                <img
                  src={artigo.imagem_destaque_url}
                  alt={artigo.titulo}
                  className="w-full h-auto object-contain"
                />
              </motion.div>
            )}

            {/* Header */}
            <motion.header
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-8"
            >
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {artigo.categoria && (
                  <span className="inline-flex items-center gap-1.5 bg-aceena-green/10 text-aceena-green px-3 py-1 rounded-full text-sm font-medium">
                    <Tag className="w-3.5 h-3.5" />
                    {artigo.categoria.nome}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatarData(artigo.data_publicacao)}
                </span>
              </div>
              <h1 className="text-2xl lg:text-4xl font-bold text-foreground leading-tight">
                {artigo.titulo}
              </h1>
              {artigo.resumo && (
                <p className="mt-4 text-lg text-muted-foreground">
                  {artigo.resumo}
                </p>
              )}
            </motion.header>

            {/* Conteúdo */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="prose prose-lg max-w-none prose-headings:text-foreground prose-p:text-muted-foreground prose-a:text-primary prose-strong:text-foreground prose-img:rounded-xl prose-img:w-full prose-img:h-auto prose-img:object-contain"
              dangerouslySetInnerHTML={{ __html: artigo.conteudo_html }}
            />
          </article>
        </div>

        {/* SEO Preview */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="font-semibold text-foreground mb-4">Preview SEO (Google)</h3>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-blue-600 text-lg hover:underline cursor-pointer truncate">
              {artigo.seo_title || artigo.titulo}
            </p>
            <p className="text-green-700 text-sm truncate">
              aceena.com.br/blog/{artigo.slug}
            </p>
            <p className="text-gray-600 text-sm mt-1 line-clamp-2">
              {artigo.seo_description || artigo.resumo || "Sem descrição"}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
